import React, { useState, useEffect } from 'react';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('super_token')}`,
  };
}

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length-1]?.[0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatHours(mins) {
  if (!mins) return '—';
  return `${Math.floor(mins/60)}h ${mins%60}m`;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function statusBadge(r) {
  if (r.status === 'weekly_off')     return { l: '🌙 Weekly Off', c: 'sad-b-off' };
  if (r.status === 'public_holiday') return { l: '🎉 Holiday',    c: 'sad-b-holiday' };
  if (r.status === 'approved_leave') return { l: '📋 On Leave',   c: 'sad-b-leave' };
  if (r.status === 'casual_auto')    return { l: '📋 Casual',     c: 'sad-b-leave' };
  if (!r.clock_in)                   return { l: '✗ Absent',      c: 'sad-b-absent' };
  if (r.late_minutes > 0)            return { l: '⏱ Late',        c: 'sad-b-late' };
  return                                    { l: '✓ Present',     c: 'sad-b-present' };
}

// ── Monthly 3-late/6-late policy tag ─────────────────────────────────
// This is a DIFFERENT concept from the per-day `status === 'half_day'`
// value above (which would mean that single day's own lateness exceeded
// 60 minutes) — this tag marks the specific day a 3rd/6th late TRIGGER
// landed, carried in `late_policy_triggers` from /salary/calculate.
// Kept visually distinct (pill, not a status badge) so the two are
// never confused. Resets every month by construction: the backend only
// ever returns triggers for the month being viewed.
function latePolicyBadgeStyle(trig) {
  const isSalary = trig.absorbed_by === 'salary';
  return {
    display: 'inline-block',
    marginLeft: 6,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: isSalary ? '#fee2e2' : '#e0e7ff',
    color: isSalary ? '#b91c1c' : '#4338ca',
  };
}

function latePolicyBadgeLabel(trig) {
  const dayLabel = trig.label === 'full_day' ? 'Full Day' : 'Half Day';
  return trig.absorbed_by === 'salary'
    ? `⏱ ${dayLabel} · ₨${trig.amount_pkr} Deducted`
    : `⏱ ${dayLabel} · Casual Used`;
}

const PRIORITIES = [
  { key: 'info',    label: 'Info',    icon: 'ℹ️', color: '#3b82f6' },
  { key: 'warning', label: 'Warning', icon: '⚠️', color: '#f59e0b' },
  { key: 'urgent',  label: 'Urgent',  icon: '🚨', color: '#ef4444' },
];

export default function SAEmployeeDetail({ employee, onClose }) {
  const [tab, setTab]           = useState('attendance');
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [month, setMonth]       = useState(new Date().getMonth() + 1);
  const [year, setYear]         = useState(new Date().getFullYear());

  // Late-policy triggers for the selected month (0.5/1-day half/full-day
  // marks), matched against report.records[].date (both are 'YYYY-MM-DD').
  const [lateTriggers, setLateTriggers] = useState([]);
  // Days the late policy charged to SALARY (not absorbed by casual) — shown
  // as absent in the Absent tile. Display only.
  const lateSalaryDays = lateTriggers
    .filter(t => t.absorbed_by === 'salary')
    .reduce((s, t) => s + (t.day_equivalent || 0), 0);

  const [history, setHistory]   = useState([]);
  const [msgEn, setMsgEn]       = useState('');
  const [msgUr, setMsgUr]       = useState('');
  const [priority, setPriority] = useState('warning');
  const [sending, setSending]   = useState(false);
  const [sendMsg, setSendMsg]   = useState('');

  useEffect(() => { loadReport(); }, [month, year]);
  useEffect(() => { if (tab === 'messages') loadHistory(); }, [tab]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const [attRes, salRes] = await Promise.all([
        fetch(`${BASE}/attendance/employee/${employee.id}?month=${month}&year=${year}`, { headers: authHeaders() }),
        // NOTE: /salary/calculate/:employee_id is keyed by the employee_id
        // CODE (e.g. BKO-EK0112), not the numeric employees.id used just
        // above for /attendance — both live on this same `employee` object,
        // easy to mix up.
        fetch(`${BASE}/salary/calculate/${employee.employee_id}?month=${month}&year=${year}`, { headers: authHeaders() }),
      ]);
      setReport(await attRes.json());
      const salData = await salRes.json();
      setLateTriggers(salData.late_policy_triggers || []);
    } catch (e) {
      console.error(e);
      setLateTriggers([]);
    }
    finally { setLoading(false); }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${BASE}/warnings/history/${employee.id}`, { headers: authHeaders() });
      const d = await res.json();
      setHistory(d.warnings || []);
    } catch (e) { console.error(e); }
  };

  const sendMessage = async () => {
    if (!msgEn.trim() || !msgUr.trim()) { setSendMsg('Both English and Urdu are required'); return; }
    setSending(true); setSendMsg('');
    try {
      const res = await fetch(`${BASE}/warnings/send`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ employee_id: employee.id, message_en: msgEn, message_ur: msgUr, priority }),
      });
      const d = await res.json();
      if (!res.ok) { setSendMsg(d.error || 'Failed to send'); return; }
      setSendMsg('Message sent — employee will see it next time they open the app');
      setMsgEn(''); setMsgUr('');
      loadHistory();
      setTimeout(() => setSendMsg(''), 4000);
    } catch { setSendMsg('Connection error'); }
    finally { setSending(false); }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const s = report?.summary || {};

  // date (YYYY-MM-DD) -> trigger info, for quick lookup per row
  const triggerByDate = {};
  lateTriggers.forEach(t => { if (t.date) triggerByDate[t.date] = t; });

  return (
    <div className="sad-overlay" onClick={onClose}>
      <div className="sad-panel" onClick={e => e.stopPropagation()}>

        <div className="sad-header">
          <div className="sad-header-left">
            <div className="sad-av" style={{ background: getAvatarColor(employee.full_name) }}>
              {getInitials(employee.full_name)}
            </div>
            <div>
              <div className="sad-name">{employee.full_name}</div>
              <div className="sad-meta">
                {employee.employee_id} · {employee.department || 'Unassigned'} · {employee.location_name}, {employee.city}
              </div>
            </div>
          </div>
          <button className="sad-close" onClick={onClose}>✕</button>
        </div>

        <div className="sad-tabs">
          <button className={`sad-tab ${tab==='attendance'?'sad-tab-on':''}`} onClick={() => setTab('attendance')}>
            🗓️ Attendance
          </button>
          <button className={`sad-tab ${tab==='messages'?'sad-tab-on':''}`} onClick={() => setTab('messages')}>
            ✉️ Send Message
          </button>
        </div>

        <div className="sad-body">
          {tab === 'attendance' ? (
            <>
              <div className="sad-period">
                <select className="sad-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
                  {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <select className="sad-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              {loading ? <div className="sad-loading">Loading…</div> : !report?.records ? (
                <div className="sad-empty">No data</div>
              ) : (
                <>
                  <div className="sad-kpis">
                    <div className="sad-kpi sad-k-green"><b>{s.present ?? 0}</b><span>Present</span></div>
                    <div className="sad-kpi sad-k-amber"><b>{s.late ?? 0}</b><span>Late</span></div>
                    <div className="sad-kpi sad-k-red"><b>{s.absent ?? 0}</b><span>Absent</span></div>
                    <div className="sad-kpi sad-k-red" style={{ borderStyle: 'dashed' }}><b>{lateSalaryDays}</b><span>Absent (Late)</span></div>
                    <div className="sad-kpi sad-k-purple"><b>{s.holidays ?? 0}</b><span>Holidays</span></div>
                    <div className="sad-kpi sad-k-navy"><b>{formatHours(s.total_minutes)}</b><span>Total Hours</span></div>
                  </div>

                  {lateTriggers.length > 0 && (
                    <div style={{
                      margin: '0 0 12px', padding: '8px 12px', borderRadius: 8,
                      background: '#fff7ed', border: '1px solid #fed7aa',
                      fontSize: 12, color: '#9a3412',
                    }}>
                      {lateTriggers.length} late-policy half/full-day {lateTriggers.length === 1 ? 'mark' : 'marks'} this month — see tagged rows below.
                    </div>
                  )}

                  {report.records.length === 0 ? (
                    <div className="sad-empty">No records for {MONTHS[month-1]} {year}</div>
                  ) : (
                    <table className="sad-table">
                      <thead>
                        <tr><th>Date</th><th>Day</th><th>In</th><th>Out</th><th>Hours</th><th>Late</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {report.records.map((r,i) => {
                          const b = statusBadge(r);
                          const d = new Date(r.date);
                          const trig = triggerByDate[r.date];
                          return (
                            <tr key={i}>
                              <td>{d.toLocaleDateString('en-PK',{day:'numeric',month:'short'})}</td>
                              <td className="sad-dim">{d.toLocaleDateString('en-PK',{weekday:'short'})}</td>
                              <td>{formatTime(r.clock_in)}</td>
                              <td>{r.clock_in && !r.clock_out ? <span className="sad-live">● Live</span> : formatTime(r.clock_out)}</td>
                              <td>{formatHours(r.total_minutes)}</td>
                              <td>{r.late_minutes > 0 ? <span className="sad-late-pill">+{r.late_minutes}m</span> : '—'}</td>
                              <td>
                                <span className={`sad-badge ${b.c}`}>{b.l}</span>
                                {trig && (
                                  <span style={latePolicyBadgeStyle(trig)} title={`Late-policy trigger #${trig.trigger_no} this month`}>
                                    {latePolicyBadgeLabel(trig)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="sad-msg-note">
                This appears as a full-screen popup the employee must acknowledge before
                using the app. Marked as sent by the Boss.
              </div>

              <label className="sad-lbl">Priority</label>
              <div className="sad-prio-row">
                {PRIORITIES.map(p => (
                  <button key={p.key}
                    className={`sad-prio ${priority===p.key?'sad-prio-on':''}`}
                    style={priority===p.key ? { background:p.color, borderColor:p.color, color:'#fff' } : {}}
                    onClick={() => setPriority(p.key)}>
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>

              <label className="sad-lbl">🇬🇧 English Message</label>
              <textarea className="sad-textarea" rows="4" value={msgEn}
                onChange={e => setMsgEn(e.target.value)}
                placeholder="e.g. Ali, please visit the office first thing tomorrow morning." />

              <label className="sad-lbl">🇵🇰 اردو پیغام</label>
              <textarea className="sad-textarea sad-urdu" rows="4" dir="rtl" value={msgUr}
                onChange={e => setMsgUr(e.target.value)}
                placeholder="مثال: علی، براہ کرم کل صبح سب سے پہلے دفتر آئیں۔" />

              {sendMsg && (
                <div className={`sad-send-msg ${sendMsg.includes('sent') ? 'sad-ok' : 'sad-err'}`}>{sendMsg}</div>
              )}

              <button className="sad-send-btn" onClick={sendMessage} disabled={sending}>
                {sending ? 'Sending…' : 'Send Message'}
              </button>

              <div className="sad-hist-title">Message History ({history.length})</div>
              {history.length === 0 ? (
                <div className="sad-empty">No messages sent yet</div>
              ) : (
                <div className="sad-hist-list">
                  {history.map((h,i) => {
                    const p = PRIORITIES.find(x => x.key === h.priority) || PRIORITIES[1];
                    return (
                      <div key={i} className="sad-hist-card" style={{ borderLeftColor: p.color }}>
                        <div className="sad-hist-top">
                          <span className="sad-hist-prio" style={{ color: p.color }}>{p.icon} {p.label}</span>
                          <span className="sad-hist-from">
                            {h.sender_role === 'super_admin' ? '⬡ Boss' : '👤 HR'} · {h.sent_by_name || '—'}
                          </span>
                          <span className={`sad-hist-ack ${h.acknowledged ? 'sad-ack-yes' : 'sad-ack-no'}`}>
                            {h.acknowledged ? '✓ Read' : '● Unread'}
                          </span>
                        </div>
                        <div className="sad-hist-msg">{h.message_en}</div>
                        <div className="sad-hist-date">
                          {new Date(h.sent_at).toLocaleString('en-PK', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit', hour12:true })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}